import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function LocationsTab() {
  const { user } = useAuth();
  const isTurbo = (user?.tier || 'free') === 'turbo';
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', country: 'India', pincode: '', type: 'Clinic', phone: '', lat: null, lon: null, place_id: null, availability_mode: 'scheduled', role_line: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiParsed, setAiParsed] = useState(false);
  const aiQuota = useAiQuota();

  const load = useCallback(() => { api.get('/dashboard/locations').then(r => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  // AI auto-parse address — TurboCharge-only, counted in 2-pulls/month quota.
  const handleParseAddress = async () => {
    if (!form.address.trim() || form.address.length < 10) return;
    if (!aiQuota.eligible) {
      setError('AI address parsing is a TurboCharge-only feature. Upgrade to unlock.');
      return;
    }
    if (aiQuota.remaining <= 0) {
      setError(`Monthly AI pull quota reached (${aiQuota.used}/${aiQuota.limit}). Resets on the 1st.`);
      return;
    }
    setAiParsing(true);
    try {
      const { data } = await api.post('/ai/parse-address', { address: form.address, name: form.name });
      if (data.ai_parsed) {
        setForm(f => ({
          ...f,
          pincode: data.pincode || f.pincode,
          city: data.city || f.city,
          state: data.state || f.state,
          country: data.country || f.country,
        }));
        setAiParsed(true);
        aiQuota.refresh();
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally { setAiParsing(false); }
  };

  const handleAdd = async () => {
    if (!form.pincode.trim()) { setError('Pincode is required'); return; }
    if (!form.state.trim()) { setError('State is required'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/dashboard/locations', form);
      setForm({ name: '', address: '', city: '', state: '', country: 'India', pincode: '', type: 'Clinic', phone: '', lat: null, lon: null, place_id: null, availability_mode: 'scheduled', role_line: '' });
      setShowForm(false); setAiParsed(false);
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  // Verified-address pick → auto-fill every field from the place. If
  // the upstream provider didn't return `state`, immediately chain into the
  // free India Post pincode lookup so state never stays blank.
  const handlePickedPlace = useCallback(async (place) => {
    if (!place) {
      // Clear button — wipe verified-pick fields so a manual entry afterwards
      // doesn't accidentally inherit a stale place_id.
      setForm(f => ({ ...f, lat: null, lon: null, place_id: null }));
      return;
    }
    setForm(f => ({
      ...f,
      name: place.name || f.name,
      address: place.address || f.address,
      city: place.city || f.city,
      state: place.state || f.state,
      pincode: place.pincode || f.pincode,
      country: place.country || f.country,
      // Verified coords — backend will skip auto-geocoding.
      lat: place.lat,
      lon: place.lon,
      place_id: place.place_id,
    }));
    setAiParsed(false);
    setError('');
    if (place.pincode && (!place.state || !place.city)) {
      try {
        const r = await api.get(`/clinics/pincode/${place.pincode}`);
        if (r.data?.valid) {
          setForm(f => ({
            ...f,
            city: f.city?.trim() ? f.city : r.data.city,
            state: f.state?.trim() ? f.state : r.data.state,
          }));
        }
      } catch (_) { /* silent */ }
    }
  }, []);

  // Pincode validator (free India Post API) → auto-fill / lock city + state
  // when the doctor types a pincode manually. Catches "Mumbai pincode +
  // Pune city" typos at source.
  const handlePincodeBlur = useCallback(async () => {
    const pc = (form.pincode || '').trim();
    if (!/^\d{6}$/.test(pc)) return;
    try {
      const r = await api.get(`/clinics/pincode/${pc}`);
      if (r.data?.valid) {
        setForm(f => ({
          ...f,
          // Only overwrite when the doctor left these blank — never override
          // explicit edits.
          city: f.city?.trim() ? f.city : r.data.city,
          state: f.state?.trim() ? f.state : r.data.state,
        }));
      }
    } catch (_) { /* silent — manual entry still works */ }
  }, [form.pincode]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this location and its schedules?')) return;
    await api.delete(`/dashboard/locations/${id}`);
    load();
  };

  const missingPincode = locations.filter(l => !l.pincode);

  return (
    <div className="space-y-4" data-testid="locations-tab">
      {missingPincode.length > 0 && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 flex items-center gap-3" data-testid="pincode-warning">
          <AlertCircle size={18} className="text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">{missingPincode.length} location{missingPincode.length > 1 ? 's' : ''} missing pincode</p>
            <p className="text-xs text-amber-600">Update {missingPincode.map(l => l.name).join(', ')} with pincode, state, and country for geo-search visibility.</p>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Locations</h3>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 rounded-full px-4 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors" data-testid="add-location-button">
          <Plus size={14} /> Add Location
        </button>
      </div>
      {showForm && (
        <div className="bg-white rounded-2xl border border-sky-200 p-5 space-y-3" data-testid="location-form">
          {/* Verified-address autocomplete — picks a real place +
              autofills every field below. Manual entry still works as
              fallback for clinics that aren't on the map yet. */}
          <div className="rounded-xl bg-sky-50/50 border border-sky-100 p-3">
            <label className="text-xs font-semibold text-sky-900 mb-2 flex items-center gap-1.5">
              <Search size={12} /> Find your clinic (recommended)
            </label>
            <ClinicSearchPicker
              onPick={handlePickedPlace}
              placeholder="Type clinic name or address — e.g. Niron Hospital Vakola"
              testid="loc-search"
            />
            <p className="mt-1.5 text-[11px] text-slate-500">
              Picking from the dropdown auto-fills every field below with verified address + map pin. Can't find your clinic? Skip and enter manually.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Name *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. HeartCare Clinic" testid="loc-name" />
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Type</label>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} data-testid="loc-type">
                <option>Clinic</option><option>Hospital</option><option>Polyclinic</option><option>Surgery Center</option><option>Institution</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Address *</label>
              <div className="relative">
                <input type="text" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                  value={form.address} onChange={e => { setForm(f => ({ ...f, address: e.target.value })); setAiParsed(false); }}
                  placeholder="Full street address" data-testid="loc-address" />
              </div>
              {/* Explicit AI parse button — Turbo-only, counted in 2-pull monthly quota. */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleParseAddress}
                  disabled={aiParsing || !aiQuota.eligible || aiQuota.remaining <= 0 || !form.address.trim() || form.address.length < 10}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ai-parse-address-btn"
                  title={!aiQuota.eligible ? 'TurboCharge-only feature' : aiQuota.remaining <= 0 ? `Monthly quota used (${aiQuota.used}/${aiQuota.limit})` : 'Auto-fill city, state, pincode'}
                >
                  {aiParsing ? <><Loader2 size={12} className="animate-spin" /> AI parsing…</> : <><Sparkles size={12} /> Auto-fill with AI</>}
                </button>
                {aiQuota.eligible ? (
                  <span className="text-[11px] text-slate-500">
                    AI pulls this month: <span className="font-semibold text-slate-700">{aiQuota.used}/{aiQuota.limit}</span>
                  </span>
                ) : (
                  <span className="text-[11px] text-slate-400">AI auto-fill is a TurboCharge-only feature</span>
                )}
              </div>
            </div>
          </div>

          {/* AI auto-filled fields */}
          {aiParsed && (
            <div className="bg-sky-50 rounded-xl p-3 flex items-center gap-2 text-xs text-sky-700" data-testid="ai-parsed-notice">
              <Sparkles size={14} className="text-sky-500" />
              <span className="font-medium">AI auto-detected location. Please confirm or edit below.</span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Pincode *" value={form.pincode} onChange={v => setForm(f => ({ ...f, pincode: v }))} onBlur={handlePincodeBlur} placeholder="e.g. 400001" testid="loc-pincode" />
            <Field label="City *" value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} placeholder="e.g. Mumbai" testid="loc-city" />
            <Field label="State *" value={form.state} onChange={v => setForm(f => ({ ...f, state: v }))} placeholder="e.g. Maharashtra" testid="loc-state" />
            <Field label="Country" value={form.country} onChange={v => setForm(f => ({ ...f, country: v }))} testid="loc-country" />
          </div>
          <Field label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="Clinic phone" testid="loc-phone" />

          {/* Availability mode — used by hospitals where the doctor only
              visits on patient request (e.g., Honorary Consultant role).
              When on_request_only is picked, the doctor surfaces a
              "Request Appointment" button on the public profile instead
              of the regular Slot Picker. ── Turbo-only feature. */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="loc-availability-mode-block">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-700">How do you see patients at this location?</label>
                {!isTurbo && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200" data-testid="loc-onreq-turbo-chip">
                    <Lock size={9} /> Turbo only
                  </span>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={() => setForm(f => ({ ...f, availability_mode: 'scheduled' }))}
                  className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${form.availability_mode !== 'on_request_only' ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  data-testid="loc-mode-scheduled">
                  Regular scheduled slots
                </button>
                <button type="button"
                  onClick={() => { if (isTurbo) setForm(f => ({ ...f, availability_mode: 'on_request_only' })); }}
                  disabled={!isTurbo}
                  title={!isTurbo ? 'Upgrade to Turbo to enable On-request mode' : ''}
                  className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${form.availability_mode === 'on_request_only' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'} ${!isTurbo ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="loc-mode-on-request">
                  By Appointment — On request only
                </button>
              </div>
              {!isTurbo && (
                <p className="text-[11px] text-violet-700 mt-2 leading-snug">
                  💎 <b>Turbo</b> plan unlocks On-request appointments + live patient-ping dashboard tab.
                </p>
              )}
              {isTurbo && form.availability_mode === 'on_request_only' && (
                <p className="text-[11px] text-amber-700 mt-2 leading-snug">
                  Patients will see a <b>Request Appointment</b> button instead of a slot picker.
                  You'll get a live notification + chime; offer a 30-min window per ask.
                </p>
              )}
            </div>
            <Field
              label="Your role at this location (optional)"
              value={form.role_line || ''}
              onChange={v => setForm(f => ({ ...f, role_line: v }))}
              placeholder='e.g. "Honorary Consultant — ENT Surgeon"'
              testid="loc-role-line"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
          <button onClick={handleAdd} disabled={saving || !form.name || !form.address || !form.city || !form.pincode || !form.state}
            className="rounded-full px-5 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50" data-testid="save-location-button">
            {saving ? 'Saving...' : 'Save Location'}
          </button>
        </div>
      )}
      {locations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">No locations yet. Add your clinic or hospital.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="locations-grid">
        {locations.map(loc => (
          <LocationCard key={loc.id} loc={loc} isTurbo={isTurbo} onChange={load} onDelete={handleDelete} />
        ))}
        </div>
      )}

      <HospitalAttachmentsSection />
    </div>
  );
}


// Per-location card with an inline mini-editor for availability_mode +
// role_line. Edits PATCH via PUT /dashboard/locations/{id} since the
// existing endpoint already accepts the full LocationInput payload.
function LocationCard({ loc, isTurbo, onChange, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState(loc.availability_mode || 'scheduled');
  const [roleLine, setRoleLine] = useState(loc.role_line || '');
  const [saving, setSaving] = useState(false);
  const isOnRequest = (loc.availability_mode || 'scheduled') === 'on_request_only';

  const save = async () => {
    setSaving(true);
    try {
      // Send the existing fields verbatim PLUS the two new ones. The
      // backend's address-change detector will see no diff and skip
      // re-geocoding, so this is cheap.
      await api.put(`/dashboard/locations/${loc.id}`, {
        name: loc.name || '',
        address: loc.address || '',
        city: loc.city || '',
        state: loc.state || '',
        country: loc.country || 'India',
        pincode: loc.pincode || '',
        type: loc.type || 'Clinic',
        phone: loc.phone || '',
        lat: loc.lat ?? null,
        lon: loc.lon ?? null,
        place_id: loc.place_id ?? null,
        availability_mode: mode,
        role_line: roleLine,
      });
      setEditing(false);
      onChange && onChange();
    } catch (e) {
      // Silent — surface via the parent's load() error path if needed.
      console.warn('Location mode save failed', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`location-${loc.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-slate-900 text-sm">{loc.name}</h4>
            {isOnRequest && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`loc-onrequest-chip-${loc.id}`}>
                On request
              </span>
            )}
          </div>
          {loc.role_line && (
            <p className="text-[11px] font-semibold text-violet-700 mt-0.5" data-testid={`loc-role-line-${loc.id}`}>{loc.role_line}</p>
          )}
          <p className="text-xs text-slate-500 mt-0.5">{loc.address}, {loc.city}{loc.state ? `, ${loc.state}` : ''} | {loc.type}</p>
          <div className="flex gap-3 mt-1">
            {loc.pincode && <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{loc.pincode}</span>}
            {loc.country && <span className="text-[10px] text-slate-400">{loc.country}</span>}
            {!loc.pincode && <span className="text-[10px] text-amber-600 font-medium">Pincode missing</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setEditing(v => !v)} className="text-slate-400 hover:text-sky-600 p-1" data-testid={`edit-loc-${loc.id}`} title="Configure availability">
            <Edit3 size={14} />
          </button>
          <button onClick={() => onDelete(loc.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`del-loc-${loc.id}`}><Trash2 size={14} /></button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3" data-testid={`loc-edit-${loc.id}`}>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-slate-700">Availability mode</p>
              {!isTurbo && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                  <Lock size={9} /> Turbo only
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => setMode('scheduled')}
                className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${mode !== 'on_request_only' ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                data-testid={`loc-mode-scheduled-${loc.id}`}>
                Regular scheduled
              </button>
              <button type="button"
                onClick={() => { if (isTurbo) setMode('on_request_only'); }}
                disabled={!isTurbo}
                title={!isTurbo ? 'Upgrade to Turbo to enable On-request mode' : ''}
                className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${mode === 'on_request_only' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'} ${!isTurbo ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`loc-mode-onreq-${loc.id}`}>
                On request only
              </button>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Your role at this location</label>
            <input
              type="text"
              value={roleLine}
              onChange={(e) => setRoleLine(e.target.value)}
              placeholder='e.g. "Honorary Consultant — ENT Surgeon"'
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-sky-500/20"
              data-testid={`loc-role-line-input-${loc.id}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving}
              className="rounded-full px-4 py-1.5 bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-50"
              data-testid={`loc-save-mode-${loc.id}`}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setMode(loc.availability_mode || 'scheduled'); setRoleLine(loc.role_line || ''); }}
              className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

